export const RONDOSHARE_COMMISSION_RATE = 0.30;
export const MAX_ESTIMATED_CURRENT_PRICE = 20_000;
export const MIN_RONDOSHARE_COMMISSION = 30;
export const MIN_SUGGESTED_FIXED_FEE = 100;
export const FIXED_FEE_DAILY_MULTIPLIER = 2;
/** Owner share of renter total when the minimum commission does not bind (1 − rate). */
export const OWNER_SHARE_RATE = 1 - RONDOSHARE_COMMISSION_RATE;
export const PREVIEW_DAYS = 3;

export enum ProductCategory {
  Tools = 0,
  Electronics = 1,
  FilmAndPhotography = 2,
  Home = 3,
  Party = 4,
  Sport = 5,
  Other = 6,
}

export type PricingStrategy = 'generous' | 'sustainable' | 'commercial';

export const PRICING_STRATEGY_MULTIPLIERS: Record<PricingStrategy, number> = {
  generous: 0.7,
  sustainable: 1,
  commercial: 1.5,
};

export const pricingStrategyMultiplier = (strategy: PricingStrategy | null | undefined): number =>
  PRICING_STRATEGY_MULTIPLIERS[strategy ?? 'sustainable'] ?? PRICING_STRATEGY_MULTIPLIERS.sustainable;

export const PRODUCT_CATEGORIES: ProductCategory[] = [
  ProductCategory.Tools,
  ProductCategory.Electronics,
  ProductCategory.FilmAndPhotography,
  ProductCategory.Home,
  ProductCategory.Party,
  ProductCategory.Sport,
  ProductCategory.Other,
];

const DAILY_RATE_PERCENT: Record<ProductCategory, number> = {
  [ProductCategory.Tools]: 0.01,
  [ProductCategory.Electronics]: 0.02,
  [ProductCategory.FilmAndPhotography]: 0.02,
  [ProductCategory.Home]: 0.01,
  [ProductCategory.Party]: 0.015,
  [ProductCategory.Sport]: 0.015,
  [ProductCategory.Other]: 0.01,
};

const DEPRECIATION_PARAMS: Record<ProductCategory, { usefulLifeYears: number; salvageRate: number }> = {
  [ProductCategory.Tools]: { usefulLifeYears: 10, salvageRate: 0.15 },
  [ProductCategory.Electronics]: { usefulLifeYears: 4, salvageRate: 0.10 },
  [ProductCategory.FilmAndPhotography]: { usefulLifeYears: 5, salvageRate: 0.15 },
  [ProductCategory.Home]: { usefulLifeYears: 12, salvageRate: 0.20 },
  [ProductCategory.Party]: { usefulLifeYears: 6, salvageRate: 0.10 },
  [ProductCategory.Sport]: { usefulLifeYears: 7, salvageRate: 0.15 },
  [ProductCategory.Other]: { usefulLifeYears: 8, salvageRate: 0.15 },
};

export const dailyRatePercent = (category: ProductCategory): number =>
  DAILY_RATE_PERCENT[category] ?? DAILY_RATE_PERCENT[ProductCategory.Other];

export const estimateCurrentPrice = (
  newPrice: number,
  ageYears: number,
  category: ProductCategory,
): number => {
  if (newPrice <= 0) return 0;
  const { usefulLifeYears, salvageRate } = DEPRECIATION_PARAMS[category] ?? DEPRECIATION_PARAMS[ProductCategory.Other];
  const salvage = newPrice * salvageRate;
  const annualDepreciation = (newPrice - salvage) / usefulLifeYears;
  const depreciated = newPrice - annualDepreciation * Math.max(0, ageYears);
  return Math.max(salvage, Math.round(depreciated));
};

export const roundMoney = (value: number): number =>
  Math.round(value * 100) / 100;

/** Suggested renter-facing daily fee (uplifted so owner net ≈ category rate of estimated value). */
export const suggestedRenterFeePerDay = (
  estimatedCurrentPrice: number,
  category: ProductCategory,
): number => {
  const ownerDaily = Math.round(estimatedCurrentPrice * dailyRatePercent(category));
  return Math.round(ownerDaily / OWNER_SHARE_RATE);
};

export const adjustedSuggestedRenterFeePerDay = (
  estimatedCurrentPrice: number,
  category: ProductCategory,
  strategy: PricingStrategy | null | undefined,
): number => Math.round(suggestedRenterFeePerDay(estimatedCurrentPrice, category) * pricingStrategyMultiplier(strategy));

/** Suggested renter-facing fixed fee (uplifted from owner suggestion). */
export const suggestedRenterFixedFee = (
  estimatedCurrentPrice: number,
  category: ProductCategory,
  strategy: PricingStrategy | null | undefined = 'sustainable',
): number => {
  const ownerDaily = Math.round(estimatedCurrentPrice * dailyRatePercent(category));
  const ownerFixed = Math.max(
    FIXED_FEE_DAILY_MULTIPLIER * ownerDaily,
    MIN_SUGGESTED_FIXED_FEE * pricingStrategyMultiplier(strategy),
  );
  return Math.round(ownerFixed / OWNER_SHARE_RATE);
};

export const renterSubtotal = (
  renterFixedFee: number,
  renterFeePerDay: number,
  days: number,
  quantity: number,
): number => roundMoney((renterFixedFee + renterFeePerDay * days) * quantity);

/** Platform fee = max(30% of renter subtotal, 30 CZK). */
export const rondoshareCommission = (renterSubtotalAmount: number): number =>
  Math.max(roundMoney(renterSubtotalAmount * RONDOSHARE_COMMISSION_RATE), MIN_RONDOSHARE_COMMISSION);

export const ownerFromRenter = (renterSubtotalAmount: number): number =>
  roundMoney(renterSubtotalAmount - rondoshareCommission(renterSubtotalAmount));

/** What the renter pays (fixed + daily × days). */
export const rentalTotal = (
  renterFixedFee: number,
  renterFeePerDay: number,
  days: number,
  quantity: number,
): number => renterSubtotal(renterFixedFee, renterFeePerDay, days, quantity);

export const effectivePerDay = (rentalTotalAmount: number, days: number): number =>
  days > 0 ? roundMoney(rentalTotalAmount / days) : 0;

/** Parse YYYY-MM-DD as a UTC calendar date (ms since epoch at UTC midnight). */
const parseDateOnly = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return Date.UTC(year, month - 1, day);
};

/** Inclusive day count: both start and end calendar days count. */
export const countRentalDays = (startDate: string, endDate: string): number | null => {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (start === null || end === null || end < start) return null;
  return Math.round((end - start) / 86_400_000) + 1;
};
